import React, { useEffect, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { COMPANY_NAME, FOOTER_LINKS, CURRENT_YEAR } from '@/constants';

/**
 * Main layout component that wraps all pages
 * Includes header with logo and footer with legal links
 */
const Layout: React.FC = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isInitialLoad = useRef(true); // Track if this is the initial page load
  const initialPath = useRef(location.pathname); // Track the first path loaded

  // After first navigation, mark as no longer initial load
  useEffect(() => {
    if (location.pathname !== initialPath.current) {
      isInitialLoad.current = false;
    }
  }, [location.pathname]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Only show delayed animation on home page if it's the initial visit (not navigating back)
  const showDelayedAnimation = isHomePage && isInitialLoad.current;

  return (
    <div className="min-h-screen flex flex-col bg-white font-sans text-slate-900">
      {/* Logo - Top Left */}
      <div
        className={`fixed top-6 left-6 lg:left-12 z-50 ${showDelayedAnimation ? 'fade-in-blurb-1' : 'animate-fade-in'}`}
      >
        <Link to="/" className="group flex items-center gap-3">
          <img
            src="/logo.jpg"
            alt={`${COMPANY_NAME} Logo`}
            className="w-8 h-8 md:w-12 md:h-12 rounded-full shadow-md shadow-brand-500/10"
          />
          <span className="font-display font-semibold text-lg tracking-tight text-black group-hover:text-brand-600 transition-colors">
            {COMPANY_NAME}
          </span>
        </Link>
      </div>

      {/* Main Content */}
      <main className="flex-grow flex flex-col pb-24">
        <Outlet context={{ showDelayedAnimation }} />
      </main>

      {/* Footer - Fixed at bottom, always visible on home page */}
      <footer
        className={`${isHomePage ? 'fixed' : 'sticky'} bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-100 py-3 ${showDelayedAnimation ? 'fade-in-footer' : ''}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 flex flex-row items-center justify-between w-full">
          <div className="text-slate-400 text-xs font-bold tracking-widest uppercase">
            &copy; {CURRENT_YEAR} {COMPANY_NAME}
          </div>
          <nav className="flex flex-wrap justify-end gap-x-4 gap-y-1">
            {FOOTER_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-brand-600 transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
